import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatCommonModule } from '@angular/material/core';
import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { SiaTranslatePipe } from '../../pipes/sia-translate.pipe';
import { SiaSizeDetectorService } from '../../services/size-detector.service';

export type SiaAccordionMode = 'default' | 'compact';

@Component({
  selector: 'sia-accordion',
  templateUrl: './sia-accordion.component.html',
  styleUrls: ['./sia-accordion.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatExpansionModule,
    SiaTranslatePipe,
    NgTemplateOutlet,
    MatButtonModule,
    MatCommonModule,
    MatIconModule,
  ],
  host: {
    '[class.full-screen]': 'fullScreen()',
  },
})
export class SiaAccordionComponent {
  private sizeDetectorService = inject(SiaSizeDetectorService);
  private windowSize = toSignal(this.sizeDetectorService.onResize$);

  openedChange = output<boolean>();
  icon = input<string>('');
  gridContainer = input<boolean>(false);
  fullScreen = input<boolean>(false);
  status = input<'error' | null | undefined>();
  opened = model<boolean>(false);
  title = input<string>('');
  mode = input<SiaAccordionMode>('default');
  headerInformation = input<string>('');
  footerInformation = input<string>('');

  panel = viewChild(MatExpansionPanel);

  protected panelClass = computed(() =>
    this.status() === 'error' ? 'mat-expansion-panel-invalid' : '',
  );

  private isMobile = computed(() => this.windowSize()?.isMobile ?? false);

  protected displayTitle = computed(() => {
    const t = this.title();
    if (this.isMobile() && t && t.length > 20) {
      return t.substring(0, 20).concat('...');
    }
    return t;
  });

  protected displayIcon = computed(() => this.icon() || null);

  protected bodyClass = computed(() => {
    let className = 'accordion-card-body';
    if (this.opened() || this.fullScreen()) {
      className += ' accordion-card-body--opend';
    }
    return className;
  });

  protected isCompact = computed(() => this.mode() === 'compact');

  constructor() {
    effect(() => {
      const p = this.panel();
      const isOpened = this.opened();
      if (p) {
        if (isOpened) {
          p.open();
        } else {
          p.close();
        }
      }
    });
  }

  onOpenedChange(value: boolean) {
    this.opened.set(value);
    this.openedChange.emit(value);
  }
}
